import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { announcementsApi, getImageUrl } from "../api";
import ImageLoader from "./common/ImageLoader";
import "./SingleAnnouncement.css";

const SingleAnnouncement = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [announcement, setAnnouncement] = useState(null);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const fetchAnnouncement = async () => {
      try {
        const res = await announcementsApi.getById(id);
        setAnnouncement(res.data);
      } catch (err) {
        console.error("❌ Error fetching announcement:", err);
      }
    };
    fetchAnnouncement();
  }, [id]);

  // Countdown Timer
  useEffect(() => {
    if (!announcement?.date) return;

    const interval = setInterval(() => {
      const eventDate = new Date(`${announcement.date}T${announcement.time || "00:00"}`);
      const now = new Date();
      const diff = eventDate - now;

      if (diff <= 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        clearInterval(interval);
      } else {
        setCountdown({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((diff / 1000 / 60) % 60),
          seconds: Math.floor((diff / 1000) % 60),
        });
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [announcement]);

  if (!announcement) {
    return (
      <div className="announcement-loading">
        <i className="fas fa-circle-notch fa-spin"></i> Loading announcement details...
      </div>
    );
  }

  return (
    <div className="single-announcement-container">
      <button className="back-nav-btn" onClick={() => navigate(-1)}>
        <i className="fas fa-arrow-left"></i> Back to Dashboard
      </button>

      <div className="single-announcement-card">
        <div className="single-header-section">
          <span className="announcement-badge">OFFICIAL ANNOUNCEMENT</span>
          <h1 className="single-title">{announcement.title}</h1>
          <p className="single-tagline">“Where Innovation Meets Imagination”</p>
        </div>

        <div className="single-content-grid">
          {/* Poster Showcase */}
          <div className="single-poster-col">
            <div className="single-poster-wrapper">
              <ImageLoader
                src={getImageUrl(announcement.poster)}
                alt={announcement.title}
                className="single-poster-img"
              />
            </div>
          </div>

          {/* Details & Countdown */}
          <div className="single-details-col">
            <div className="single-info-block">
              <h3><i className="fas fa-align-left"></i> About the Event</h3>
              <p>{announcement.fullDescription || announcement.shortDescription}</p>
            </div>

            <div className="single-meta-grid">
              <div className="meta-card">
                <i className="far fa-calendar-alt meta-card-icon"></i>
                <div>
                  <span className="meta-label">Date</span>
                  <span className="meta-val">{announcement.date || "TBA"}</span>
                </div>
              </div>

              <div className="meta-card">
                <i className="far fa-clock meta-card-icon"></i>
                <div>
                  <span className="meta-label">Time</span>
                  <span className="meta-val">{announcement.time || "To be announced"}</span>
                </div>
              </div>

              <div className="meta-card">
                <i className="fas fa-map-marker-alt meta-card-icon"></i>
                <div>
                  <span className="meta-label">Location</span>
                  <span className="meta-val">{announcement.location || "Online"}</span>
                </div>
              </div>

              <div className="meta-card">
                <i className="fas fa-users-cog meta-card-icon"></i>
                <div>
                  <span className="meta-label">Organized By</span>
                  <span className="meta-val">{announcement.organizedBy || "DIET ACM Chapter"}</span>
                </div>
              </div>
            </div>

            {/* Countdown Box */}
            <div className="single-countdown-card">
              <h4><i className="fas fa-hourglass-half"></i> Event Countdown</h4>
              <div className="countdown-tiles">
                <div className="countdown-tile">
                  <span className="tile-num">{countdown.days}</span>
                  <span className="tile-label">Days</span>
                </div>
                <div className="countdown-tile">
                  <span className="tile-num">{countdown.hours}</span>
                  <span className="tile-label">Hours</span>
                </div>
                <div className="countdown-tile">
                  <span className="tile-num">{countdown.minutes}</span>
                  <span className="tile-label">Mins</span>
                </div>
                <div className="countdown-tile">
                  <span className="tile-num">{countdown.seconds}</span>
                  <span className="tile-label">Secs</span>
                </div>
              </div>
            </div>

            {/* Registration Action */}
            {announcement.registrationLink && (
              <div className="single-action-row">
                <a
                  href={announcement.registrationLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="register-btn"
                >
                  <i className="fas fa-external-link-alt"></i> Open Registration Link
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SingleAnnouncement;
